/* eslint-disable arrow-body-style */
import {computed,  makeObservable, observable} from "mobx";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {ProcessConfiguration} from "../config/process-configuration";
import {ProcessStatusConfiguration} from "../config/process-status-configuration";
import {AbstractModel} from "./abstract-model";
import {dateToEsri, esriToDate} from "../utils/esri-utils";


export interface ProjectProcessProps {
    objectId?: number;
    guid?: string;
    id?: number;
    type: string;
    version: number;
    projectId?: number;
    title?: string;
    startDate?: Date;
    dueDate?: Date;
    closedOn?: Date;
    statusId?: number;
    progress?: number;
    creator?: string;
    createdAt?: Date;
    editor?: string;
    updatedAt?: Date;
    config?: ProcessConfiguration;
}

/**
 * Prozess für Projekte
 *
 * Serializer siehe serializers/project-process-json.ts
 */
@JsonObject()
export class ProjectProcess extends AbstractModel {

    static readonly comparableAttributes: string[] = ["STATUS_ID", "CLOSED_ON"];
    static readonly prototypeAttributes: string[] = [
        "OBJECTID", "GUID", "MASSNAHME_GUID", "TITEL", "START_DATE", "DUE_DATE", "CLOSED_ON", "STATUS_ID",
        "PROGRESS", "TYPE", "VERSION", "CREATED_USER", "CREATED_AT", "UPDATED_USER", "UPDATED_AT"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "OBJECTID"],
        ["guid", "GUID"],
        ["id", "PROCESS_ID"],
        ["projectId", "PROJ_ID"],
        ["title", "TITEL"],
        ["startDate", "START_DATE"],
        ["dueDate", "DUE_DATE"],
        ["closedOn", "CLOSED_ON"],
        ["statusId", "STATUS_ID"],
        ["progress", "PROGRESS"],
        ["type", "TYPE"],
        ["version", "VERSION"],
        ["creator", "CREATED_USER"],
        ["createdAt", "CREATED_AT"],
        ["editor", "UPDATED_USER"],
        ["updatedAt", "UPDATED_AT"]
    ]);

    class = "ProjectProcess";
    typeLabel = "Prozess";

    /**
     * Unique identifier process
     */
    @JsonProperty({name: "GUID"})
        guid: string;

    /**
     * Prozess ID
     */
    @JsonProperty({name: "PROCESS_ID"})
        id: number;

    /**
     * Projektschlüssel
     */
    @JsonProperty({name: "PROJ_ID"})
        projectId: number;

    /**
     * Titel
     */
    @JsonProperty({name: "TITEL"})
        title: string;

    /**
     * Startdatum
     */
    @JsonProperty({
        name: "START_DATE",
        beforeDeserialize: esriToDate,
        afterSerialize: dateToEsri
    })
        startDate: Date;

    /**
     * Enddatum
     */
    @JsonProperty({
        name: "DUE_DATE",
        beforeDeserialize: esriToDate,
        afterSerialize: dateToEsri
    })
        dueDate: Date;

    /**
     * Abgeschlossen am
     */
    @JsonProperty({
        name: "CLOSED_ON",
        beforeDeserialize: esriToDate,
        afterSerialize: dateToEsri
    })
        closedOn: Date;

    /**
     * Projektstatus
     *
     */
    @JsonProperty({name: "STATUS_ID"})
        statusId: number;

    /**
     * Prozesstyp
     */
    @JsonProperty({name: "TYPE"})
        type: string;

    /**
     * Prozessversion
     */
    @JsonProperty({name: "VERSION"})
        version: number;

    /**
     * Fortschritt in %
     */
    @JsonProperty({name: "PROGRESS"})
        progress: number;

    /**
     * process configuration
     */
    config: ProcessConfiguration;


    constructor(props?: ProjectProcessProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        // make observable after setting properties to avoid 'strict' messages
        makeObservable(this, {
            objectId: observable, // do observe to reload when object is saved
            guid: observable,
            type: observable,
            version: observable,
            projectId: observable,
            title: observable,
            startDate: observable,
            dueDate: observable,
            closedOn: observable,
            statusId: observable,
            progress: observable,
            config: observable,
            isClosed: computed,
            isError: computed,
            isProjectAlterable: computed,
            statusConfig: computed,
            updatedKeys: computed,

            creator: observable,
            createdAt: observable,
            editor: observable,
            updatedAt: observable,
            isNewRecord: computed
        });
    }

    get comparableAttributes(): string[] {
        return ProjectProcess.comparableAttributes;
    }

    get isAlterable(): boolean {
        return true;
    }

    /**
     * If the process is in a closed state
     */
    get isClosed(): boolean {
        return this.statusConfig ? this.statusConfig.isClosed : false;
    }

    /**
     * If the process is in a closed state
     */
    get isError(): boolean {
        return this.statusConfig ? this.statusConfig.type === "error" : false;
    }

    /**
     * If project properties are allowed to change.
     * Depends on current process status.
     */
    get isProjectAlterable(): boolean {
        return this.statusConfig?.canChangeProject === true;
    }

    /**
     * Returns configuration for current state
     */
    get statusConfig(): ProcessStatusConfiguration | undefined {
        return this.config?.states?.find((config) => config.id === this.statusId);
    }

    /**
     * Update Attributes in feature and return.
     * Preparation to send changed process values to service
     */
    get updatedKeys(): object {
        const keys = {};
        // not all attributes can be changed
        keys["STATUS_ID"] = this.statusId;
        // set close date when state is a close state
        if (this.statusConfig?.isClosed) {
            keys["CLOSED_ON"] = Date.now();
        }
        return keys;
    }

    /**
     * Get all states with valid transitions from the given state
     *
     * @param statusId
     */
    getValidTransitionStates(statusId: number): number[] {
        // search for transitions with this state as start state
        const validTransitions = this.config?.transitions?.filter((trans) => trans[0] === statusId);
        // return all target states of found transitions
        return validTransitions ? validTransitions.map((t) => t[1]) : [];
    }

}

